import { effectiveConsent } from '../../shared/privacyConsent.ts'
import { getConsentRegionMode, subscribeConsentRegionMode } from './consentRegion.ts'
export type AnalyticsConsent = 'unset' | 'granted' | 'denied'
export type MarketingConsent = 'unset' | 'granted' | 'denied'

export type AnalyticsEventName =
  | 'page_view'
  | 'view_item_list'
  | 'select_item'
  | 'add_to_cart'
  | 'view_cart'
  | 'begin_checkout'
  | 'add_shipping_info'
  | 'add_payment_info'
  | 'purchase'
  | 'build_box_start'
  | 'select_flavour'
  | 'remove_flavour'
  | 'surprise_me'
  | 'box_complete'
  | 'delivery_ineligible'
  | 'view_vault'
  | 'vault_flavour_interest'
  | 'support_start'
  | 'support_submit'

export const ANALYTICS_CONSENT_STORAGE_KEY = 'sg.analytics-consent.v1'
export const ANALYTICS_CONSENT_MAX_AGE_DAYS = 180
export const MARKETING_CONSENT_STORAGE_KEY = 'sg.marketing-consent.v1'
export const MARKETING_CONSENT_MAX_AGE_DAYS = 180

const ANALYTICS_CONSENT_EVENT = 'sugar-gremlin:analytics-consent-change'
const MARKETING_CONSENT_EVENT = 'sugar-gremlin:marketing-consent-change'
const CONSENT_MAX_AGE_MS = ANALYTICS_CONSENT_MAX_AGE_DAYS * 24 * 60 * 60 * 1000
const MARKETING_CONSENT_MAX_AGE_MS = MARKETING_CONSENT_MAX_AGE_DAYS * 24 * 60 * 60 * 1000
const GA_SCRIPT_ID = 'sugar-gremlin-ga4'
const PRODUCTION_HOSTS = new Set(['sugargremlin.co.nz', 'www.sugargremlin.co.nz'])
const ALLOWED_UTM_PARAMETERS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const
const SAFE_UTM_VALUE = /^[a-z0-9]+(?:_[a-z0-9]+)*$/

type StoredAnalyticsConsent = {
  version: 1
  choice: Exclude<AnalyticsConsent, 'unset'>
  updatedAt: number
  expiresAt: number
}

type StoredMarketingConsent = {
  version: 1
  choice: Exclude<MarketingConsent, 'unset'>
  updatedAt: number
  expiresAt: number
}

type Gtag = (...args: unknown[]) => void

declare global {
  interface Window {
    dataLayer?: unknown[]
    gtag?: Gtag
  }
}

let analyticsActive = false
let activeMeasurementId: string | null = null
let gaConfigured = false
const onceKeys = new Set<string>()
const volatileConsentChoices = new Map<string, AnalyticsConsent>()

function safeStorage(): Storage | null {
  try {
    return window.localStorage
  } catch {
    return null
  }
}

function parseStoredConsent(raw: string | null): StoredAnalyticsConsent | null {
  if (!raw) return null

  try {
    const parsed = JSON.parse(raw) as Partial<StoredAnalyticsConsent>
    if (parsed.version !== 1) return null
    if (parsed.choice !== 'granted' && parsed.choice !== 'denied') return null
    if (typeof parsed.updatedAt !== 'number' || typeof parsed.expiresAt !== 'number') return null
    return parsed as StoredAnalyticsConsent
  } catch {
    return null
  }
}

function storedConsent(storageKey: string): AnalyticsConsent {
  if (typeof window === 'undefined') return 'unset'

  const volatile = volatileConsentChoices.get(storageKey)
  if (volatile === 'granted' || volatile === 'denied') return volatile

  const storage = safeStorage()
  if (!storage) return 'unset'

  const stored = parseStoredConsent(storage.getItem(storageKey))
  if (!stored) return 'unset'

  if (Date.now() >= stored.expiresAt) {
    try {
      storage.removeItem(storageKey)
    } catch {
      // Storage expiry is best effort. The effective state is still unset.
    }
    return 'unset'
  }

  return stored.choice
}

export function getStoredAnalyticsConsent(): AnalyticsConsent {
  return storedConsent(ANALYTICS_CONSENT_STORAGE_KEY)
}

export function getStoredMarketingConsent(): MarketingConsent {
  return storedConsent(MARKETING_CONSENT_STORAGE_KEY)
}

export function getAnalyticsConsent(): AnalyticsConsent {
  return effectiveConsent(getStoredAnalyticsConsent(), getConsentRegionMode())
}

export function getMarketingConsent(): MarketingConsent {
  return effectiveConsent(getStoredMarketingConsent(), getConsentRegionMode())
}

export function setAnalyticsConsent(choice: Exclude<AnalyticsConsent, 'unset'>): void {
  if (typeof window === 'undefined') return

  const storage = safeStorage()
  const now = Date.now()
  const value: StoredAnalyticsConsent = {
    version: 1,
    choice,
    updatedAt: now,
    expiresAt: now + CONSENT_MAX_AGE_MS,
  }

  let persisted = false
  try {
    if (storage) {
      storage.setItem(ANALYTICS_CONSENT_STORAGE_KEY, JSON.stringify(value))
      persisted = true
    }
  } catch {
    // The volatile choice below still applies for the current document.
  }
  if (persisted) volatileConsentChoices.delete(ANALYTICS_CONSENT_STORAGE_KEY)
  else volatileConsentChoices.set(ANALYTICS_CONSENT_STORAGE_KEY, choice)

  window.dispatchEvent(new CustomEvent<AnalyticsConsent>(ANALYTICS_CONSENT_EVENT, { detail: choice }))
}

export function setMarketingConsent(choice: Exclude<MarketingConsent, 'unset'>): void {
  if (typeof window === 'undefined') return

  const storage = safeStorage()
  const now = Date.now()
  const value: StoredMarketingConsent = {
    version: 1,
    choice,
    updatedAt: now,
    expiresAt: now + MARKETING_CONSENT_MAX_AGE_MS,
  }

  let persisted = false
  try {
    if (storage) {
      storage.setItem(MARKETING_CONSENT_STORAGE_KEY, JSON.stringify(value))
      persisted = true
    }
  } catch {
    // The volatile choice below still applies for the current document.
  }
  if (persisted) volatileConsentChoices.delete(MARKETING_CONSENT_STORAGE_KEY)
  else volatileConsentChoices.set(MARKETING_CONSENT_STORAGE_KEY, choice)

  window.dispatchEvent(new CustomEvent<MarketingConsent>(MARKETING_CONSENT_EVENT, { detail: choice }))
}

function subscribeEffectiveConsent<T extends AnalyticsConsent | MarketingConsent>(
  storageKey: string,
  eventName: string,
  getter: () => T,
  listener: (consent: T) => void,
): () => void {
  if (typeof window === 'undefined') return () => undefined

  const emitEffective = () => listener(getter())
  const handleStorage = (event: StorageEvent) => {
    if (event.key !== storageKey) return
    volatileConsentChoices.delete(storageKey)
    emitEffective()
  }

  const unsubscribeRegion = subscribeConsentRegionMode(emitEffective)
  window.addEventListener(eventName, emitEffective)
  window.addEventListener('storage', handleStorage)

  return () => {
    unsubscribeRegion()
    window.removeEventListener(eventName, emitEffective)
    window.removeEventListener('storage', handleStorage)
  }
}

export function subscribeEffectiveAnalyticsConsent(listener: (consent: AnalyticsConsent) => void): () => void {
  return subscribeEffectiveConsent(ANALYTICS_CONSENT_STORAGE_KEY, ANALYTICS_CONSENT_EVENT, getAnalyticsConsent, listener)
}

export function subscribeEffectiveMarketingConsent(listener: (consent: MarketingConsent) => void): () => void {
  return subscribeEffectiveConsent(MARKETING_CONSENT_STORAGE_KEY, MARKETING_CONSENT_EVENT, getMarketingConsent, listener)
}

// Backwards-compatible names remain effective-consent subscriptions.
export function subscribeAnalyticsConsent(listener: (consent: AnalyticsConsent) => void): () => void {
  return subscribeEffectiveAnalyticsConsent(listener)
}

export function subscribeMarketingConsent(listener: (consent: MarketingConsent) => void): () => void {
  return subscribeEffectiveMarketingConsent(listener)
}

export function getAnalyticsEnvironment(hostname = typeof window === 'undefined' ? '' : window.location.hostname): 'production' | 'staging' {
  return PRODUCTION_HOSTS.has(hostname.toLowerCase()) ? 'production' : 'staging'
}

function normaliseMeasurementId(value: string | undefined): string | null {
  const trimmed = value?.trim().toUpperCase()
  if (!trimmed || !/^G-[A-Z0-9]+$/.test(trimmed)) return null
  return trimmed
}

export function selectGa4MeasurementId(hostname: string, productionMeasurementId: string | undefined, stagingMeasurementId: string | undefined): string | null {
  return getAnalyticsEnvironment(hostname) === 'production'
    ? normaliseMeasurementId(productionMeasurementId)
    : normaliseMeasurementId(stagingMeasurementId)
}

export function getGa4MeasurementId(hostname = typeof window === 'undefined' ? '' : window.location.hostname): string | null {
  return selectGa4MeasurementId(
    hostname,
    import.meta.env.VITE_GA4_PRODUCTION_MEASUREMENT_ID,
    import.meta.env.VITE_GA4_STAGING_MEASUREMENT_ID,
  )
}

function setGaDisabled(measurementId: string, disabled: boolean): void {
  ;(window as unknown as Record<string, unknown>)[`ga-disable-${measurementId}`] = disabled
}

function ensureGtag(): Gtag {
  window.dataLayer ??= []
  if (!window.gtag) {
    window.gtag = function gtag(..._args: unknown[]) {
      window.dataLayer?.push(arguments)
    }
  }
  return window.gtag
}

function safeCampaignValue(value: string | null): string | null {
  if (!value || value.length > 80) return null
  const normalised = value.trim().toLowerCase()
  return SAFE_UTM_VALUE.test(normalised) ? normalised : null
}

function sanitiseUrl(rawUrl: string): string {
  try {
    const url = new URL(rawUrl, window.location.origin)
    const safeUrl = new URL(url.pathname || '/', url.origin)
    for (const parameter of ALLOWED_UTM_PARAMETERS) {
      const value = safeCampaignValue(url.searchParams.get(parameter))
      if (value) safeUrl.searchParams.set(parameter, value)
    }
    return safeUrl.href
  } catch {
    return `${window.location.origin}${window.location.pathname}`
  }
}

function safeReferrer(rawReferrer: string): string {
  if (!rawReferrer) return ''
  try {
    return new URL(rawReferrer).origin
  } catch {
    return ''
  }
}

function safePagePath(): string {
  const url = new URL(sanitiseUrl(window.location.href))
  return `${url.pathname}${url.search}`
}

function injectGaScript(measurementId: string): void {
  const existing = document.getElementById(GA_SCRIPT_ID) as HTMLScriptElement | null
  if (existing?.src.includes(`id=${encodeURIComponent(measurementId)}`)) return
  existing?.remove()

  const script = document.createElement('script')
  script.id = GA_SCRIPT_ID
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`
  script.dataset.analyticsEnvironment = getAnalyticsEnvironment()
  document.head.appendChild(script)
}

function removeAnalyticsCookies(): void {
  const cookieNames = document.cookie
    .split(';')
    .map((cookie) => cookie.split('=')[0]?.trim())
    .filter((name): name is string => Boolean(name) && name.startsWith('_ga'))

  const domains = [window.location.hostname]
  if (window.location.hostname === 'sugargremlin.co.nz' || window.location.hostname.endsWith('.sugargremlin.co.nz')) {
    domains.push('.sugargremlin.co.nz')
  }

  for (const name of cookieNames) {
    document.cookie = `${name}=; Max-Age=0; path=/; SameSite=Lax`
    for (const domain of domains) {
      document.cookie = `${name}=; Max-Age=0; path=/; domain=${domain}; SameSite=Lax`
    }
  }
}

function sendCurrentPageView(): void {
  if (!analyticsActive || !activeMeasurementId || !window.gtag) return

  window.gtag('event', 'page_view', {
    page_location: sanitiseUrl(window.location.href),
    page_path: safePagePath(),
    page_title: document.title,
  })
}

export function startAnalytics(): boolean {
  if (typeof window === 'undefined' || getAnalyticsConsent() !== 'granted') return false

  const measurementId = getGa4MeasurementId()
  if (!measurementId) return false

  if (analyticsActive && activeMeasurementId === measurementId) return true

  activeMeasurementId = measurementId
  analyticsActive = true
  setGaDisabled(measurementId, false)

  const gtag = ensureGtag()
  gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  })
  gtag('consent', 'update', {
    analytics_storage: 'granted',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  })

  if (!gaConfigured) {
    gtag('js', new Date())
    gtag('config', measurementId, {
      send_page_view: false,
      allow_google_signals: false,
      allow_ad_personalization_signals: false,
      page_location: sanitiseUrl(window.location.href),
      page_referrer: safeReferrer(document.referrer),
      debug_mode: getAnalyticsEnvironment() === 'staging',
    })
    gaConfigured = true
  }

  injectGaScript(measurementId)
  sendCurrentPageView()
  return true
}

export function stopAnalytics({ preserveCookies = false }: { preserveCookies?: boolean } = {}): void {
  if (typeof window === 'undefined') return

  const measurementId = activeMeasurementId ?? getGa4MeasurementId()
  if (measurementId) {
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'denied',
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',
      })
    }
    setGaDisabled(measurementId, true)
  }

  document.getElementById(GA_SCRIPT_ID)?.remove()
  if (!preserveCookies) removeAnalyticsCookies()
  analyticsActive = false
}

export function trackAnalyticsEvent(eventName: AnalyticsEventName, parameters: Record<string, unknown> = {}): boolean {
  if (!analyticsActive || getAnalyticsConsent() !== 'granted' || !activeMeasurementId || !window.gtag) return false

  window.gtag('event', eventName, parameters)
  return true
}

export function trackAnalyticsEventOnce(key: string, eventName: AnalyticsEventName, parameters: Record<string, unknown> = {}): boolean {
  if (onceKeys.has(key)) return false
  const sent = trackAnalyticsEvent(eventName, parameters)
  if (sent) onceKeys.add(key)
  return sent
}

export function isAnalyticsActive(): boolean {
  return analyticsActive
}
